import type { Product as ProductLiquid } from '@grafikr/shopify-typescript/type/liquid/Product';
import type { ProductVariant as ProductVariantLiquid } from '@grafikr/shopify-typescript/type/liquid/ProductVariant';
import type { Product as ProductJson } from '@grafikr/shopify-typescript/type/json/Product';
import type { ProductVariant as ProductVariantJson } from '@grafikr/shopify-typescript/type/json/ProductVariant';
import Algolia from 'utils/algolia';
import { NonNullish } from 'utils/nullish-check';

type Product = ProductLiquid | ProductJson;
type ProductVariant = ProductVariantLiquid | ProductVariantJson;

const algolia = new Algolia();

const dataLayerHelper = {
  getProductCategoryType: (product: Product) => {
    let productType: string;
    if ('product_type' in product) {
      productType = product.product_type;
    } else {
      productType = product.type;
    }

    const [category, type] = productType.split('_');

    return { category, type };
  },

  getVariantPrice: (variant: ProductVariant) => {
    if (typeof variant.price === 'number') {
      return variant.price / 100;
    }

    return parseFloat(variant.price);
  },

  getVariantName: (product: Product, variant: ProductVariant) => {
    if ('name' in variant) {
      return variant.name;
    }

    return `${product.title} - ${variant.title}`;
  },

  getVariantAvailability: (variant: ProductVariant) => {
    if ('available' in variant) {
      return variant.available;
    }

    return true;
  },

  getProductImageUrl: (product: Product) => {
    if ('featured_image' in product) {
      return product.featured_image;
    }

    return product.images.at(0)?.src ?? null;
  },

  getProductFields: (product: Product, variant: ProductVariant) => {
    const productUrl = new URL(`/products/${product.handle}`, window.origin);
    const variantUrl = new URL(`/products/${product.handle}?variant=${variant.id}`, window.origin);

    return {
      sku: variant.sku,
      variantId: variant.id,
      variantTitle: dataLayerHelper.getVariantName(product, variant),
      variantImage: dataLayerHelper.getProductImageUrl(product),
      variantUrl: variantUrl.toString(),
      price: dataLayerHelper.getVariantPrice(variant),
      productTitle: product.title,
      productType: dataLayerHelper.getProductCategoryType(product).type,
      productVendor: product.vendor,
      productUrl: productUrl.toString(),
      productImage: dataLayerHelper.getProductImageUrl(product),
      productOnSale: variant.price <= (variant.compare_at_price ?? 0),
      productOutOfStock: !dataLayerHelper.getVariantAvailability(variant),
    };
  },

  getProductItem: (product: Product, variant: ProductVariant) => ({
    id: product.id,
    name: dataLayerHelper.getVariantName(product, variant),
    brand: product.vendor,
    category: dataLayerHelper.getProductCategoryType(product).category,
    variant: variant.title,
    price: dataLayerHelper.getVariantPrice(variant),
    metric1: false,
    metric2: !dataLayerHelper.getVariantAvailability(variant),
  }),
};

document.addEventListener('data:cart:view', () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'viewCart',
  });
});

document.addEventListener('data:product:view_variant', ({ detail: { product, variant } }) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'viewContent',
    ...dataLayerHelper.getProductFields(product, variant),
    ecommerce: {
      currencyCode: Shopify.currency.active,
      detail: {
        actionField: { list: '' },
        products: [dataLayerHelper.getProductItem(product, variant)],
      },
    },
  });
});

document.addEventListener(
  'data:product:added_to_cart',
  ({ detail: { context, product, variant } }) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'addToCart',
      ...dataLayerHelper.getProductFields(product, variant),
      ecommerce: {
        currentCode: Shopify.currency.active,
        actionType: context.type,
        add: {
          actionField: { list: '' },
          products: [dataLayerHelper.getProductItem(product, variant)],
        },
      },
    });
  },
);

document.addEventListener('data:product:click', ({ detail: { context, product, variant } }) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'productClick',
    ...dataLayerHelper.getProductFields(product, variant),
    ecommerce: {
      currentCode: Shopify.currency.active,
      actionType: context.type,
      add: {
        actionField: { list: '' },
        products: [dataLayerHelper.getProductItem(product, variant)],
      },
    },
  });
});

document.addEventListener('data:collection:viewed', ({ detail: { products } }) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'collection_page',
    ecommerce: {
      currencyCode: Shopify.currency.active,
      impressions: products.map((product) => ({
        name: product.title,
        id: product.id,
        variant_id: NonNullish(product.variants.at(0)).id,
        variant_sku: NonNullish(product.variants.at(0)).sku,
        handle: product.handle,
        price: product.price / 100,
        availability: product.available,
        product_type: product.type,
        vendor: product.vendor,
      })),
    },
  });
});
